import {
  ISymbolTopicLivePrices,
  SYM_SUB_KEY_UIMARKET_FAVOURITES_ITEM,
  SYM_SUB_KEY_UIMARKET_POSITIONS_ITEM,
  SYM_SUB_KEY_UIMARKET_SIGNALS_ITEM,
  SYM_SUB_KEY_UIMARKET_TOPLIST_ITEM_TOPGAINERS,
  SYM_SUB_KEY_UIMARKET_TOPLIST_ITEM_TOPLOSERS,
  SYM_SUB_KEY_UIMARKET_TOPLIST_ITEM_TOPPICKS,
  SYM_SUB_KEY_UIMARKET_WATCHLIST_ITEM,
  symbolTopicSubscription
} from '@aquariuxfintech/aqx-trader-sdk-config/socket';

import { ESymbolCategory, ISymbol, marketAPIController } from '@aqx-core/common/api/market';
import { IOrder, orderAPIController } from '@aqx-core/common/api/order';
import { ISignal, signalAPIController } from '@aqx-core/common/api/signal';
import { EWatchlistCategory, watchlistAPIController } from '@aqx-core/common/api/watchlist';
import { getSignalUniqueId, sortSignals } from '@aqx-core/common/services/signal/utils';
import { marketComponentConvertion } from '@aqx-core/common/utils/market';
import { EMarketComponentItems } from '@aqx-core/common/utils/market/types';
import { ESortType, sortObjAscDescByKey } from '@aqx-core/common/utils/sorting';

import { marketService } from './service';

class MarketViewController {
  private mappedTopPickSymbols: Record<string, ISymbol> = {};
  private mappedTopGainerSymbols: Record<string, ISymbol> = {};
  private mappedTopLoserSymbols: Record<string, ISymbol> = {};
  private mappedMyFavouriteSymbols: Record<string, ISymbol> = {};
  private mappedWatchlistSymbols: Record<string, ISymbol> = {};
  private mappedMyPositionSymbols: Record<string, IOrder> = {};
  private mappedMyPositionOrders: Record<string, IOrder> = {};
  private mappedSignals: Record<string, ISignal> = {};

  private topPickSymbolIds: string[] = [];
  private topGainerSymbolIds: string[] = [];
  private topLoserSymbolIds: string[] = [];
  private myPositionSymbolIds: string[] = [];
  private myPositionOrdersIds: string[] = [];
  private myFavouriteSymbolIds: string[] = [];
  private watchlistSymbolIds: string[] = [];
  private signalIds: string[] = [];

  private isCallingAllSymbolAPI = false;
  private isCallingPositionsAPI = false;
  private isCallingSignalsAPI = false;

  private topPickSymbolToSubscribeSet = new Set<string>();
  private topGainerSymbolToSubscribeSet = new Set<string>();
  private topLoserSymbolToSubscribeSet = new Set<string>();
  private myPositionSymbolToSubscribeSet = new Set<string>();
  private myFavouriteSymbolToSubscribeSet = new Set<string>();
  private watchlistSymbolToSubscribeSet = new Set<string>();
  private signalSymbolToSubscribeSet = new Set<string>();

  /** Latest live price per symbol; flushed at most once per frame to limit store updates. */
  private pendingPriceUpdates = new Map<string, ISymbolTopicLivePrices>();
  private priceFlushTimer: ReturnType<typeof setTimeout> | null = null;

  private static readonly PRICE_UPDATE_FLUSH_MS = 16;

  public init = () => {
    /**
     * Watch list symbols must always be retrieved first in order to feed data for the  `symbolType` value of My Positions table
     */
    this.getSymbolListByFilter(EMarketComponentItems.ALL, true).then(() => {
      this.getSymbolCategories();

      this.getSymbolListByFilter(EMarketComponentItems.POSITIONS, true);
      this.getSymbolListByFilter(EMarketComponentItems.TOP_PICK, true);
      this.getSymbolListByFilter(EMarketComponentItems.TOP_GAINER, true);
      this.getSymbolListByFilter(EMarketComponentItems.TOP_LOSER, true);
      this.getSymbolListByFilter(EMarketComponentItems.FAVOURITES, true);
      this.getSymbolListByFilter(EMarketComponentItems.SIGNALS, true);
    });
  };

  public reset = () => {
    this.unsubscribeAllSymbolTopic();

    if (this.priceFlushTimer !== null) {
      clearTimeout(this.priceFlushTimer);
      this.priceFlushTimer = null;
    }
    this.pendingPriceUpdates.clear();

    // Clear all cached data to prevent stale data across different user sessions
    this.mappedTopPickSymbols = {};
    this.mappedTopGainerSymbols = {};
    this.mappedTopLoserSymbols = {};
    this.mappedMyFavouriteSymbols = {};
    this.mappedWatchlistSymbols = {};
    this.mappedMyPositionSymbols = {};
    this.mappedMyPositionOrders = {};
    this.mappedSignals = {};

    this.topPickSymbolIds = [];
    this.topGainerSymbolIds = [];
    this.topLoserSymbolIds = [];
    this.myPositionSymbolIds = [];
    this.myPositionOrdersIds = [];
    this.myFavouriteSymbolIds = [];
    this.watchlistSymbolIds = [];
    this.signalIds = [];

    this.isCallingAllSymbolAPI = false;
    this.isCallingPositionsAPI = false;
    this.isCallingSignalsAPI = false;

    marketService.resetContext();
  };

  public resetMarketScreenTabs = () => {
    marketService.setMarketMoverCurrentTab(undefined);
    marketService.setWatchlistCurrentTab(undefined);
  };

  public setMarketMoverCurrentTab = (tab: string | undefined) => {
    marketService.setMarketMoverCurrentTab(tab);
  };

  public setWatchlistCurrentTab = (tab: string | undefined) => {
    marketService.setWatchlistCurrentTab(tab);
  };

  public setMarketScreenTab = (tab: string | undefined) => {
    marketService.setMarketScreenTab(tab);
  };

  public refreshFavourites = async () => {
    await this.getMyFavourites();
  };

  // User Actions
  public initSymbolCategories = async () => {
    if (marketService.context.symbolCategories.length === 0) {
      this.getSymbolCategories();
    }
  };

  public refreshSymbolCategories = async () => {
    await this.getSymbolCategories();
  };

  public onSymbolCategoryChanged = (filter: EMarketComponentItems, symbolCategory: ESymbolCategory) => {
    let currentSelectedSymbolCategory = undefined;

    switch (filter) {
      case EMarketComponentItems.TOP_PICK:
        currentSelectedSymbolCategory = marketService.context.selectedTopPickSymbolCategory;
        break;
      case EMarketComponentItems.TOP_GAINER:
        currentSelectedSymbolCategory = marketService.context.selectedTopGainerSymbolCategory;
        break;
      case EMarketComponentItems.TOP_LOSER:
        currentSelectedSymbolCategory = marketService.context.selectedTopLoserSymbolCategory;
        break;
      case EMarketComponentItems.FAVOURITES:
        currentSelectedSymbolCategory = marketService.context.selectedMyFavouriteSymbolCategory;
        break;
      case EMarketComponentItems.ALL:
        currentSelectedSymbolCategory = marketService.context.selectedWatchListSymbolCategory;
        break;
      case EMarketComponentItems.POSITIONS:
        currentSelectedSymbolCategory = marketService.context.selectedMyPositionSymbolCategory;
        break;
      case EMarketComponentItems.SIGNALS:
        currentSelectedSymbolCategory = marketService.context.selectedSignalSymbolCategory;
        break;
      default:
        break;
    }

    if (currentSelectedSymbolCategory === symbolCategory) {
      return;
    }

    marketService.setSelectedSymbolCategory(filter, symbolCategory);
    this.filterSymbols(filter, symbolCategory);
  };

  public getMarketTopList = async (filter: EMarketComponentItems) => {
    await this.getTopLists(filter);
  };

  // API Calls
  public getSymbolCategories = async () => {
    await marketAPIController.getMarketSymbolCategories({
      onSuccess: (result) => {
        const symbolTypeList = result?.symbolTypeList ?? [];
        marketService.setSymbolCategories(symbolTypeList);
      },
      onError: () => {}
    });
  };

  public getSymbolListByFilter = async (filter: EMarketComponentItems, initial?: boolean) => {
    if (filter === EMarketComponentItems.ALL) {
      await this.getAllSymbols(initial);
    } else if (filter === EMarketComponentItems.POSITIONS) {
      await this.getOpenPositions(initial);
    } else if (filter === EMarketComponentItems.FAVOURITES) {
      await this.getMyFavourites(initial);
    } else if (filter === EMarketComponentItems.SIGNALS) {
      await this.getSignals(initial);
    } else {
      await this.getTopLists(filter, initial);
    }
  };

  private getAllSymbols = async (initial?: boolean) => {
    if (this.isCallingAllSymbolAPI) {
      return;
    }

    const filter = EMarketComponentItems.ALL;

    marketService.setLoading(filter, !initial);
    this.isCallingAllSymbolAPI = true;

    await marketAPIController.getAllSymbolsWithoutHideEffect({
      onSuccess: (symbols) => {
        this.massageSymbols(filter, symbols);
      },
      onError: () => {}
    });

    marketService.setLoading(filter, false);
    this.isCallingAllSymbolAPI = false;
  };

  /**
   *  Temporarily utilize the watchlist API to retrieve the list, as the market API with mini-chart
   *  is currently limited to 5 items. This is a workaround for the issue described in
   *  https://aquariux.atlassian.net/browse/MEX-232
   *  For more detail, please look at the ticket and comments
   */
  private getTopLists = async (filter: EMarketComponentItems, initial?: boolean) => {
    marketService.setLoading(filter, initial ?? false);
    const convertedFilter = marketComponentConvertion(filter);

    if (convertedFilter) {
      await watchlistAPIController.getWatchlistWithMiniChart(
        { code: convertedFilter, abortable: false },
        {
          onSuccess: (symbols) => {
            this.massageSymbols(filter, symbols);
          },
          onError: () => {}
        }
      );
    }

    marketService.setLoading(filter, false);
  };

  private getMyFavourites = async (initial?: boolean) => {
    const filter = EMarketComponentItems.FAVOURITES;

    marketService.setLoading(filter, initial ?? false);
    await watchlistAPIController.getWatchlistWithoutMiniChart(
      { code: EWatchlistCategory.FAVOURITES },
      {
        onSuccess: (symbols) => {
          this.massageSymbols(filter, symbols);
        },
        onError: () => {}
      }
    );

    marketService.setLoading(filter, false);
  };

  private getOpenPositions = async (initial?: boolean) => {
    if (this.isCallingPositionsAPI) {
      return;
    }

    const filter = EMarketComponentItems.POSITIONS;

    marketService.setLoading(filter, !initial);
    this.isCallingPositionsAPI = true;

    await orderAPIController.getAllOpenPositions(
      {
        symbol: undefined
      },
      {
        onSuccess: (orders) => {
          this.massageOpenOrders(filter, orders);
        },
        onError: () => {}
      }
    );

    marketService.setLoading(filter, false);
    this.isCallingPositionsAPI = false;
  };

  private getSignals = async (initial?: boolean) => {
    if (this.isCallingSignalsAPI) {
      return;
    }

    const filter = EMarketComponentItems.SIGNALS;

    marketService.setLoading(filter, !initial);
    this.isCallingSignalsAPI = true;

    await signalAPIController.getFavouriteSignals({
      onSuccess: async (favoriteSignals) => {
        if (favoriteSignals && favoriteSignals.length > 0) {
          this.massageSignals(filter, favoriteSignals);
        } else {
          await signalAPIController.getAllSignals({
            onSuccess: (allSignals) => {
              this.massageSignals(filter, allSignals);
            },
            onError: () => {}
          });
        }
      },
      onError: async () => {
        await signalAPIController.getAllSignals({
          onSuccess: (allSignals) => {
            this.massageSignals(filter, allSignals);
          },
          onError: () => {}
        });
      }
    });

    marketService.setLoading(filter, false);
    this.isCallingSignalsAPI = false;
  };

  // Sockets
  public subscribeSymbolInViewport = (filter: EMarketComponentItems, symbol: string) => {
    switch (filter) {
      case EMarketComponentItems.TOP_PICK:
        this.subscribe(this.topPickSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_TOPLIST_ITEM_TOPPICKS);
        break;
      case EMarketComponentItems.TOP_GAINER:
        this.subscribe(this.topGainerSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_TOPLIST_ITEM_TOPGAINERS);
        break;
      case EMarketComponentItems.TOP_LOSER:
        this.subscribe(this.topLoserSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_TOPLIST_ITEM_TOPLOSERS);
        break;
      case EMarketComponentItems.FAVOURITES:
        this.subscribe(this.myFavouriteSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_FAVOURITES_ITEM);
        break;
      case EMarketComponentItems.ALL:
        this.subscribe(this.watchlistSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_WATCHLIST_ITEM);
        break;
      case EMarketComponentItems.POSITIONS:
        this.subscribe(this.myPositionSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_POSITIONS_ITEM);
        break;
      case EMarketComponentItems.SIGNALS:
        this.subscribe(this.signalSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_SIGNALS_ITEM);
        break;
      default:
        break;
    }
  };

  public unsubscribeSymbolInViewport = (filter: EMarketComponentItems, symbol: string) => {
    switch (filter) {
      case EMarketComponentItems.TOP_PICK:
        this.unsubscribe(this.topPickSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_TOPLIST_ITEM_TOPPICKS);
        break;
      case EMarketComponentItems.TOP_GAINER:
        this.unsubscribe(this.topGainerSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_TOPLIST_ITEM_TOPGAINERS);
        break;
      case EMarketComponentItems.TOP_LOSER:
        this.unsubscribe(this.topLoserSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_TOPLIST_ITEM_TOPLOSERS);
        break;
      case EMarketComponentItems.FAVOURITES:
        this.unsubscribe(this.myFavouriteSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_FAVOURITES_ITEM);
        break;
      case EMarketComponentItems.ALL:
        this.unsubscribe(this.watchlistSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_WATCHLIST_ITEM);
        break;
      case EMarketComponentItems.POSITIONS:
        this.unsubscribe(this.myPositionSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_POSITIONS_ITEM);
        break;
      case EMarketComponentItems.SIGNALS:
        this.unsubscribe(this.signalSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_SIGNALS_ITEM);
        break;
      default:
        break;
    }
  };

  private subscribe = (symbolSet: Set<string>, symbol: string, subscriptionKey: string) => {
    if (!symbolSet.has(symbol)) {
      symbolTopicSubscription.subscribe(symbol, subscriptionKey, this.updateSymbolPrice);
      symbolSet.add(symbol);
    }
  };

  private unsubscribe = (symbolSet: Set<string>, symbol: string, subscriptionKey: string) => {
    if (symbolSet.has(symbol)) {
      symbolTopicSubscription.unsubscribe(symbol, subscriptionKey, this.updateSymbolPrice);
      symbolSet.delete(symbol);
    }
  };

  private unsubscribeAllSymbolTopic = () => {
    for (const symbol of this.topPickSymbolToSubscribeSet) {
      this.unsubscribe(this.topPickSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_TOPLIST_ITEM_TOPPICKS);
    }

    for (const symbol of this.topGainerSymbolToSubscribeSet) {
      this.unsubscribe(this.topGainerSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_TOPLIST_ITEM_TOPGAINERS);
    }

    for (const symbol of this.topLoserSymbolToSubscribeSet) {
      this.unsubscribe(this.topLoserSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_TOPLIST_ITEM_TOPLOSERS);
    }

    for (const symbol of this.myFavouriteSymbolToSubscribeSet) {
      this.unsubscribe(this.myFavouriteSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_FAVOURITES_ITEM);
    }

    for (const symbol of this.watchlistSymbolToSubscribeSet) {
      this.unsubscribe(this.watchlistSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_WATCHLIST_ITEM);
    }

    for (const symbol of this.myPositionSymbolToSubscribeSet) {
      this.unsubscribe(this.myPositionSymbolToSubscribeSet, symbol, SYM_SUB_KEY_UIMARKET_POSITIONS_ITEM);
    }
  };

  // Private functions
  private schedulePriceUpdateFlush = () => {
    if (this.priceFlushTimer !== null) {
      return;
    }
    this.priceFlushTimer = setTimeout(() => {
      this.priceFlushTimer = null;
      const batch = this.pendingPriceUpdates;
      this.pendingPriceUpdates = new Map();
      for (const [id, livePrices] of batch) {
        this.applySymbolPriceUpdate(id, livePrices);
      }
    }, MarketViewController.PRICE_UPDATE_FLUSH_MS);
  };

  private updateSymbolPrice = (id: string, livePrices: ISymbolTopicLivePrices) => {
    this.pendingPriceUpdates.set(id, livePrices);
    this.schedulePriceUpdateFlush();
  };

  private applySymbolPriceUpdate = (id: string, livePrices: ISymbolTopicLivePrices) => {
    const priceUpdate = {
      lastPrice: livePrices.liveLastPrice,
      high: livePrices.liveHigh,
      low: livePrices.liveLow,
      bid: livePrices.liveSell,
      ask: livePrices.liveBuy,
      spread: livePrices.liveSpread
    };

    if (marketService.context.mappedTopPickSymbols[id]) {
      const updatedSymbols = {
        ...marketService.context.mappedTopPickSymbols,
        [id]: {
          ...marketService.context.mappedTopPickSymbols[id],
          ...priceUpdate
        }
      };
      marketService.setMappedSymbols(EMarketComponentItems.TOP_PICK, updatedSymbols);
    }

    if (marketService.context.mappedTopGainerSymbols[id]) {
      const updatedSymbols = {
        ...marketService.context.mappedTopGainerSymbols,
        [id]: {
          ...marketService.context.mappedTopGainerSymbols[id],
          ...priceUpdate
        }
      };
      marketService.setMappedSymbols(EMarketComponentItems.TOP_GAINER, updatedSymbols);
    }

    if (marketService.context.mappedTopLoserSymbols[id]) {
      const updatedSymbols = {
        ...marketService.context.mappedTopLoserSymbols,
        [id]: {
          ...marketService.context.mappedTopLoserSymbols[id],
          ...priceUpdate
        }
      };
      marketService.setMappedSymbols(EMarketComponentItems.TOP_LOSER, updatedSymbols);
    }

    if (marketService.context.mappedMyFavouriteSymbols[id]) {
      const updatedSymbols = {
        ...marketService.context.mappedMyFavouriteSymbols,
        [id]: {
          ...marketService.context.mappedMyFavouriteSymbols[id],
          ...priceUpdate
        }
      };
      marketService.setMappedSymbols(EMarketComponentItems.FAVOURITES, updatedSymbols);
    }

    if (marketService.context.mappedWatchlistSymbols[id]) {
      const updatedSymbols = {
        ...marketService.context.mappedWatchlistSymbols,
        [id]: {
          ...marketService.context.mappedWatchlistSymbols[id],
          ...priceUpdate
        }
      };
      marketService.setMappedSymbols(EMarketComponentItems.ALL, updatedSymbols);
    }

    // For positions, we need to handle IOrder objects differently
    if (marketService.context.mappedMyPositionSymbols[id]) {
      const updatedPositions = {
        ...marketService.context.mappedMyPositionSymbols,
        [id]: {
          ...marketService.context.mappedMyPositionSymbols[id],
          ...priceUpdate
        }
      };
      marketService.setMappedOpenPositionsBySymbol(updatedPositions);
    }
  };

  public preLoadMarketSymbolList = (filter: EMarketComponentItems) => {
    switch (filter) {
      case EMarketComponentItems.TOP_PICK:
        marketService.setMappedSymbols(filter, this.mappedTopPickSymbols);
        marketService.setSymbolIds(filter, this.topPickSymbolIds);
        this.filterSymbols(filter, marketService.context.selectedTopPickSymbolCategory);
        break;
      case EMarketComponentItems.TOP_GAINER:
        marketService.setMappedSymbols(filter, this.mappedTopGainerSymbols);
        marketService.setSymbolIds(filter, this.topGainerSymbolIds);
        this.filterSymbols(filter, marketService.context.selectedTopGainerSymbolCategory);
        break;
      case EMarketComponentItems.TOP_LOSER:
        marketService.setMappedSymbols(filter, this.mappedTopLoserSymbols);
        marketService.setSymbolIds(filter, this.topLoserSymbolIds);
        this.filterSymbols(filter, marketService.context.selectedTopLoserSymbolCategory);
        break;
      case EMarketComponentItems.FAVOURITES:
        marketService.setMappedSymbols(filter, this.mappedMyFavouriteSymbols);
        marketService.setSymbolIds(filter, this.myFavouriteSymbolIds);
        this.filterSymbols(filter, marketService.context.selectedMyFavouriteSymbolCategory);
        break;
      case EMarketComponentItems.ALL:
        marketService.setMappedSymbols(filter, this.mappedWatchlistSymbols);
        marketService.setSymbolIds(filter, this.watchlistSymbolIds);
        this.filterSymbols(filter, marketService.context.selectedWatchListSymbolCategory);
        break;
      case EMarketComponentItems.POSITIONS:
        marketService.setMappedOpenPositionsBySymbol(this.mappedMyPositionSymbols);
        marketService.setMappedOpenPositionByOrderId(this.mappedMyPositionOrders);
        marketService.setSymbolIds(filter, this.myPositionSymbolIds);
        marketService.setOrderIds(this.myPositionOrdersIds);

        this.filterSymbols(filter, marketService.context.selectedMyPositionSymbolCategory);
        break;
      case EMarketComponentItems.SIGNALS:
        marketService.setMappedSignals(this.mappedSignals);
        marketService.setSymbolIds(filter, this.signalIds);
        this.filterSymbols(filter, marketService.context.selectedSignalSymbolCategory);
        break;
      default:
        break;
    }
  };

  private filterSymbols = (filter: EMarketComponentItems, symbolCategory: ESymbolCategory) => {
    if (symbolCategory === ESymbolCategory.ALL) {
      marketService.resetFilteredSymbols(filter);
    } else {
      marketService.filterSymbolsByCategory(filter, symbolCategory);
    }
  };

  // Handling for Symbols - ISymbol
  private massageSymbols = (filter: EMarketComponentItems, symbols: ISymbol[] | null | undefined) => {
    if (symbols && symbols.length > 0) {
      // Top Picks, Top Gainers, Top Losers preserve backend order
      // (sorted by admin time, positive change %, negative change % respectively)
      const shouldPreserveOrder =
        filter === EMarketComponentItems.TOP_PICK ||
        filter === EMarketComponentItems.TOP_GAINER ||
        filter === EMarketComponentItems.TOP_LOSER ||
        filter === EMarketComponentItems.FAVOURITES;

      const processedSymbols = shouldPreserveOrder
        ? symbols
        : sortObjAscDescByKey([...symbols], 'symbol', ESortType.ASC);

      const { symbolObjects, symbolIds } = this.getSymbolListAndIds(processedSymbols);

      this.setSymbolsAndIds(symbolObjects, symbolIds, filter);
    } else {
      this.setSymbolsAndIds({}, [], filter);
    }
  };

  private massageSignals = (filter: EMarketComponentItems, signals: ISignal[] | null | undefined) => {
    if (signals && signals.length > 0) {
      const originalCount = signals.length;

      // Apply sorting by published/modified date (newest first) and limit to 20
      const sortedSignals = sortSignals(signals).slice(0, 20);

      const { signalObjects, signalIds } = this.getSignalListAndIds(sortedSignals);

      this.setSignalsAndIds(signalObjects, signalIds, filter);
      marketService.setTotalSignalCount(originalCount);
    } else {
      this.setSignalsAndIds({}, [], filter);
      marketService.setTotalSignalCount(0);
    }
  };

  private setSymbolsAndIds = (symbols: Record<string, ISymbol>, ids: string[], filter: EMarketComponentItems) => {
    switch (filter) {
      case EMarketComponentItems.TOP_PICK:
        this.mappedTopPickSymbols = symbols;
        this.topPickSymbolIds = ids;
        break;
      case EMarketComponentItems.TOP_GAINER:
        this.mappedTopGainerSymbols = symbols;
        this.topGainerSymbolIds = ids;
        break;
      case EMarketComponentItems.TOP_LOSER:
        this.mappedTopLoserSymbols = symbols;
        this.topLoserSymbolIds = ids;
        break;
      case EMarketComponentItems.FAVOURITES:
        this.mappedMyFavouriteSymbols = symbols;
        this.myFavouriteSymbolIds = ids;
        break;
      case EMarketComponentItems.ALL:
        this.mappedWatchlistSymbols = symbols;
        this.watchlistSymbolIds = ids;
        break;
      default:
        break;
    }

    this.preLoadMarketSymbolList(filter);
  };

  private setSignalsAndIds = (symbols: Record<string, ISignal>, ids: string[], filter: EMarketComponentItems) => {
    this.mappedSignals = symbols;
    this.signalIds = ids;

    this.preLoadMarketSymbolList(filter);
  };

  private getSymbolListAndIds = (symbolList: ISymbol[]) => {
    const symbolObjects = symbolList.reduce(
      (objects, symbol) => {
        objects[symbol.symbol] = symbol;
        return objects;
      },
      {} as Record<string, ISymbol>
    );

    const symbolIds = symbolList.map((symbol) => symbol.symbol);

    return { symbolObjects, symbolIds };
  };

  private getSignalListAndIds = (signalList: ISignal[]) => {
    const signalObjects = signalList.reduce(
      (objects, signal) => {
        const uniqueKey = getSignalUniqueId(signal);
        objects[uniqueKey] = signal;
        return objects;
      },
      {} as Record<string, ISignal>
    );

    const signalIds = signalList.map((signal) => getSignalUniqueId(signal));

    return { signalObjects, signalIds };
  };

  public getPositionSymbolByOrderId = (orderId: string) => {
    return this.mappedMyPositionOrders[orderId]?.symbol || undefined;
  };

  public getMarketTopListSymbol = (id: string, marketComponentType: EMarketComponentItems): ISymbol | undefined => {
    let mappedSymbols: Record<string, ISymbol>;
    switch (marketComponentType) {
      case EMarketComponentItems.TOP_PICK:
        mappedSymbols = marketService.context.mappedTopPickSymbols;
        break;
      case EMarketComponentItems.TOP_GAINER:
        mappedSymbols = marketService.context.mappedTopGainerSymbols;
        break;
      case EMarketComponentItems.TOP_LOSER:
        mappedSymbols = marketService.context.mappedTopLoserSymbols;
        break;
      default:
        mappedSymbols = marketService.context.mappedTopPickSymbols;
    }
    return mappedSymbols[id];
  };

  // Handling for Open Positions - IOrder
  private massageOpenOrders = (filter: EMarketComponentItems, orders: IOrder[] | null | undefined) => {
    if (orders && orders.length > 0) {
      const { symbolObjects, symbolIds } = this.getOrderListAndIdsBySymbol(orders);
      this.setOrdersAndIdsBySymbol(symbolObjects, symbolIds, filter);
      const { orderObjects, orderIds } = this.getOrderListAndIdsByOrder(orders);

      this.setOrdersAndIdsByOrder(orderObjects, orderIds, filter);
    } else {
      this.setOrdersAndIdsBySymbol({}, [], filter);
    }
  };

  private setOrdersAndIdsBySymbol = (
    symbols: Record<string, IOrder & { symbolType: ESymbolCategory }>,
    ids: string[],
    filter: EMarketComponentItems
  ) => {
    this.mappedMyPositionSymbols = symbols;
    this.myPositionSymbolIds = ids;

    this.preLoadMarketSymbolList(filter);
  };

  private setOrdersAndIdsByOrder = (
    orders: Record<string, IOrder & { symbolType: ESymbolCategory }>,
    ids: string[],
    filter: EMarketComponentItems
  ) => {
    this.mappedMyPositionOrders = orders;
    this.myPositionOrdersIds = ids;

    this.preLoadMarketSymbolList(filter);
  };

  private getOrderListAndIdsBySymbol = (orderList: IOrder[]) => {
    const symbolObjects = orderList.reduce(
      (objects, order) => {
        const watchlistSymbol = this.mappedWatchlistSymbols[order.symbol];
        const enrichedOrder = {
          ...order,
          symbolType: watchlistSymbol?.type || order?.symbolType || ESymbolCategory.EQUITIES,
          description: order.description || watchlistSymbol?.description
        };

        objects[order.symbol] = enrichedOrder;
        return objects;
      },
      {} as Record<string, IOrder & { symbolType: ESymbolCategory }>
    );

    const symbolIds = orderList.map((symbol) => symbol.symbol);

    return { symbolObjects, symbolIds };
  };

  private getOrderListAndIdsByOrder = (orderList: IOrder[]) => {
    const orderObjects = orderList.reduce(
      (objects, order) => {
        const watchlistSymbol = this.mappedWatchlistSymbols[order.symbol];
        const enrichedOrder = {
          ...order,
          symbolType: watchlistSymbol?.type || order?.symbolType || ESymbolCategory.EQUITIES,
          description: order.description || watchlistSymbol?.description
        };

        objects[order.orderId] = enrichedOrder;
        return objects;
      },
      {} as Record<string, IOrder & { symbolType: ESymbolCategory }>
    );

    const orderIds = orderList.map((order) => order.orderId);
    return { orderObjects, orderIds };
  };
}

export const marketViewController = new MarketViewController();
